import { UserRequester } from '@cobliteam/api-lib';
import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import { Environment } from '@cobliteam/react-environments/src/Environments';
import { Button, Typography } from '@cobliteam/ui/components';
import Checkbox from '@cobliteam/ui/form/Checkbox';
import Label from '@cobliteam/ui/form/Label';
import { useMemo, useState } from 'react';

const REQUIRED_PERMISSIONS = ['create-update-delete:users-and-invites', 'cobler:admin', 'basic:permission'];

const env = Environment.getInstance().getEnvironment();
const userRequester = new UserRequester({ host: env.herbieApiHost });

const PermissionsData = () => {
  const [loading, setLoading] = useState(false);
  const { getUser, getDefaultRoles } = useUser();

  const user = getUser();
  const { data: defaultRoles } = getDefaultRoles();

  const [selectedUserPermissions, setSelectedUserPermissions] = useState<string[]>(user.grantedPermissions);

  const handlePermissionChange = (permission: string) => {
    setSelectedUserPermissions((prev) => (prev.includes(permission) ? prev.filter((p) => p !== permission) : [...prev, permission]));
  };

  const saveUserPermissions = (permissions: string[]) => {
    setLoading(true);
    userRequester
      .saveUserPermissions(user.id, user.role, [], [...permissions, ...REQUIRED_PERMISSIONS])
      .then(() => {
        window.location.reload();
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const allPermissions = useMemo(() => new Set<string>((defaultRoles || []).flatMap((role) => role.permissions)), [defaultRoles]);

  const nonRequiredPermissions = useMemo(() => Array.from(allPermissions).filter((permission) => !REQUIRED_PERMISSIONS.includes(permission)), [allPermissions]);

  const handleSelectAll = () => {
    if (nonRequiredPermissions.every((p) => selectedUserPermissions.includes(p))) {
      setSelectedUserPermissions(selectedUserPermissions.filter((p) => REQUIRED_PERMISSIONS.includes(p)));
    } else {
      setSelectedUserPermissions([...new Set([...selectedUserPermissions.filter((p) => REQUIRED_PERMISSIONS.includes(p)), ...nonRequiredPermissions])]);
    }
  };

  const groupedPermissions = useMemo(() => {
    const groups = Array.from(allPermissions).reduce<Record<string, string[]>>((acc, permission) => {
      const [type, name] = permission.split(':');
      acc[type] = [...(acc[type] || []), name].sort();
      return acc;
    }, {});
    return groups;
  }, [allPermissions]);

  const isCobler = user.role === 'cobler';

  return (
    <div className='w-full space-y-4'>
      <div className='flex items-center justify-between'>
        <div>
          <Typography variant='heading-1' color='default'>
            Permissões
          </Typography>
          {isCobler && (
            <Typography variant='body' color='danger'>
              <strong>Cobler tem permissões especiais!</strong>
            </Typography>
          )}
        </div>
        <Button loading={loading} disabled={loading} onClick={() => saveUserPermissions(selectedUserPermissions)}>
          Salvar
        </Button>
      </div>

      <div className='space-y-4'>
        <div className='flex items-center gap-2'>
          <Checkbox id='select-all' checked={nonRequiredPermissions.every((p) => selectedUserPermissions.includes(p))} onCheckedChange={handleSelectAll} />
          <Label htmlFor='select-all'>Selecionar todas</Label>
        </div>

        <div className='flex flex-col gap-6'>
          {Object.entries(groupedPermissions)
            .sort(([typeA], [typeB]) => typeA.localeCompare(typeB))
            .map(([type, permissions]) => (
              <section key={type} className='space-y-2'>
                <Typography variant='heading-2'>{type}</Typography>
                <div className='grid grid-cols-2 gap-x-4 gap-y-2'>
                  {permissions.map((name) => {
                    const fullPermission = `${type}:${name}`;
                    return (
                      <div key={fullPermission} className='flex items-center gap-2'>
                        <Checkbox
                          id={fullPermission}
                          disabled={REQUIRED_PERMISSIONS.includes(fullPermission)}
                          checked={selectedUserPermissions.includes(fullPermission)}
                          onCheckedChange={() => handlePermissionChange(fullPermission)}
                        />
                        <Label htmlFor={fullPermission}>{name}</Label>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
        </div>
      </div>
    </div>
  );
};

export default PermissionsData;
